import { forwardRef } from "react";
import { Bell } from "lucide-react";
import { Button } from "../../components/Button";

// 頂列的通知鈴鐺。它只負責顯示,資料與開關都在 useNotifications 裡。
//
// --------------------------------------------------------------------------
// 未讀數不可以只靠顏色
// --------------------------------------------------------------------------
// 一顆紅點只說得出「有東西」,說不出「有幾則」,而且對看不出紅色的人
// 與螢幕閱讀器等於什麼都沒說。所以這裡兩件事都做:
//
//   看得見的:徽章裡是**真的數字**,不是一顆色點。
//   讀得到的:按鈕的 aria-label 寫成「通知(3 則未讀)」,數字與「未讀」
//             兩個字都在名稱裡。
//
// 徽章本身 aria-hidden —— 名稱已經講過一次,不然螢幕閱讀器會把數字讀兩遍。
//
// 底色用 --danger 而不是金黃:金黃 #EBC12F 在白底上只有約 1.7:1,
// 拿它當未讀指示等於看得到讀不清,而且金黃在這套系統裡只當裝飾。

/** 超過這個數就不再寫實際值。三位數的徽章會把鈴鐺撐歪,而且「幾百則」本身沒有意義。 */
const MAX_SHOWN = 99;

export interface NotificationBellProps {
  unreadCount: number;
  /** 面板開著沒有。鍵盤使用者按下去要有回饋,所以會寫進 aria-expanded。 */
  open: boolean;
  onToggle: () => void;
}

export const NotificationBell = forwardRef<HTMLButtonElement, NotificationBellProps>(function NotificationBell(
  { unreadCount, open, onToggle },
  ref,
) {
  const hasUnread = unreadCount > 0;
  // 沒有未讀時不掛一顆寫著 0 的徽章 —— 那會讓人以為有東西沒看。
  const label = hasUnread ? `通知(${unreadCount} 則未讀)` : "通知(沒有未讀)";

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="sm"
      onClick={onToggle}
      aria-label={label}
      aria-expanded={open}
      icon={<Bell aria-hidden="true" size={18} />}
      className="relative shrink-0"
    >
      {hasUnread ? (
        <span
          data-testid="nq-unread-badge"
          aria-hidden="true"
          className={
            "tabular absolute -right-0.5 -top-0.5 min-w-[1.1rem] rounded-full border " +
            "border-[var(--surface)] bg-[var(--danger)] px-1 text-[10px] font-semibold " +
            "leading-4 text-[var(--on-danger)]"
          }
        >
          {unreadCount > MAX_SHOWN ? `${MAX_SHOWN}+` : unreadCount}
        </span>
      ) : null}
    </Button>
  );
});
